const contentsProperty: unique symbol = Symbol('contents');
const resultProperty: unique symbol = Symbol('result');

type JsonOk<SuccessType> = { '@result': 'OK' } & (
  | { '@ok': SuccessType }
  | SuccessType
);
type JsonErr<ErrorType> = { '@result': 'ERR' } & (
  | { '@err': ErrorType }
  | ErrorType
);

export type JsonResult<SuccessType, ErrorType> =
  | JsonOk<SuccessType>
  | JsonErr<ErrorType>;

type OkResult<T> = { [resultProperty]: 'OK'; [contentsProperty]: T };
type ErrResult<T> = { [resultProperty]: 'ERR'; [contentsProperty]: T };

export type Result<SuccessType, ErrorType> =
  | OkResult<SuccessType>
  | ErrResult<ErrorType>;

function isJsonOk<SuccessType>(
  maybeJsonOk: unknown
): maybeJsonOk is JsonOk<SuccessType> {
  return (
    typeof maybeJsonOk === 'object' &&
    maybeJsonOk !== null &&
    '@result' in maybeJsonOk &&
    maybeJsonOk['@result'] === 'OK'
  );
}

function isJsonErr<ErrorType>(
  maybeJsonErr: unknown
): maybeJsonErr is JsonErr<ErrorType> {
  return (
    typeof maybeJsonErr === 'object' &&
    maybeJsonErr !== null &&
    '@result' in maybeJsonErr &&
    maybeJsonErr['@result'] === 'ERR'
  );
}

export function isOk<SuccessType, ErrorType>(
  result: Result<SuccessType, ErrorType>
): result is OkResult<SuccessType> {
  return result[resultProperty] === 'OK';
}

export function isErr<SuccessType, ErrorType>(
  result: Result<SuccessType, ErrorType>
): result is ErrResult<ErrorType> {
  return result[resultProperty] === 'ERR';
}

export function parseJsonResult<SuccessType, ErrorType>(
  json: JsonResult<SuccessType, ErrorType>
): Result<SuccessType, ErrorType> {
  if (isJsonOk<SuccessType>(json)) {
    const { '@result': resultKind, ...rest } = json;
    const contents = '@ok' in rest ? rest['@ok'] : rest;
    return {
      [resultProperty]: resultKind,
      [contentsProperty]: contents as SuccessType,
    };
  } else if (isJsonErr<ErrorType>(json)) {
    const { '@result': resultKind, ...rest } = json;
    const contents = '@err' in rest ? rest['@err'] : rest;
    return {
      [resultProperty]: resultKind,
      [contentsProperty]: contents as ErrorType,
    };
  } else {
    throw new Error('Provided value was not a valid Result');
  }
}

export function unwrapOrElseThrow<T>(result: Result<T, unknown>): T {
  if (isErr(result)) {
    throw new Error('Tried to unwrap an Err Result');
  }
  return result[contentsProperty];
}

export function unwrapErrOrElseThrow<T>(result: Result<unknown, T>): T {
  if (isOk(result)) {
    throw new Error('Tried to unwrap the error of an Ok Result');
  }
  return result[contentsProperty];
}

export function resultOf<SuccessType, ErrorType>(
  value: SuccessType
): Result<SuccessType, ErrorType> {
  return {
    [resultProperty]: 'OK',
    [contentsProperty]: value,
  };
}

export function resultOfErr<SuccessType, ErrorType>(
  error: ErrorType
): Result<SuccessType, ErrorType> {
  return {
    [resultProperty]: 'ERR',
    [contentsProperty]: error,
  };
}
